/**
 * @file Layout.tsx
 * @author Arunkumar Annamalai
 * @version 1.0.0
 * @description Main layout component for the Shay Suite Admin application.
 *              This file provides the application shell with responsive sidebar navigation,
 *              theme switching functionality, and adaptive layout based on route requirements.
 *              It handles both full layout (with sidebar) and header-only layout modes.
 * @created Aug-2025
 * @lastModified Aug-2025
 */

// src/components/Layout.tsx

import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  CssBaseline,
  styled,
  Theme,
  MenuItem, // For profile dropdown (optional, but useful)
  Menu, // For profile dropdown (optional)
} from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import MenuIcon from '@mui/icons-material/Menu';
import Sidebar from './Sidebar';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext'; // Re-added for role checking
import { APP_CONFIG } from '../helpers/constants';

// Layout configuration constants
const drawerWidth = 240;
const closedDrawerWidth = 60; // Must match Sidebar's closed width

/**
 * Styled AppBar component with smooth transitions for sidebar open/close states
 * Handles responsive behavior and theme-based styling
 */
const AppBarStyled = styled(AppBar, {
  shouldForwardProp: (prop: string) => prop !== 'open' && prop !== 'headerOnly',
})<{ open?: boolean; headerOnly?: boolean }>(({ theme, open, headerOnly }: { theme: Theme; open?: boolean; headerOnly?: boolean }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(headerOnly && {
    marginLeft: 0,
    width: '100%',
  }),
  ...(!headerOnly && open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  ...(!headerOnly && !open && {
    marginLeft: closedDrawerWidth,
    width: `calc(100% - ${closedDrawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen, // Use enteringScreen for closing too for smoother effect
    }),
  }),
}));

/**
 * Props interface for the Layout component
 */
interface LayoutProps {
  children: React.ReactNode;  // Content to be rendered in the main area
  headerOnly?: boolean;        // Whether to show only header without sidebar
}

/**
 * Main layout component that provides the application shell
 * 
 * This component:
 * - Manages responsive sidebar navigation
 * - Provides theme switching functionality
 * - Handles route-based page titles
 * - Supports both full layout and header-only modes
 * - Implements smooth transitions for sidebar states
 * 
 * @param {LayoutProps} props - Component props containing children and layout mode
 * @returns {JSX.Element} The complete layout structure
 */
const Layout: React.FC<LayoutProps> = ({ children, headerOnly = false }) => {
  // State management for layout interactions
  const [open, setOpen] = useState(false); // State to control sidebar open/close
  const [themeMenuAnchor, setThemeMenuAnchor] = useState<null | HTMLElement>(null);
  const { themeMode, setThemeMode, isDarkMode } = useTheme();
  const { isRootAdmin } = useAuth(); // Check if user is root admin
  const navigate = useNavigate();
  const location = useLocation();

  // Determine if we should show the sidebar
  const shouldShowSidebar = !headerOnly && isRootAdmin;

  /**
   * Determines the page title based on the current route
   * Provides dynamic titles for different sections of the application
   * 
   * @returns {string} The appropriate page title for the current route
   */
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard') {
      return 'Dashboard';
    } else if (path === '/customers') {
      return 'Customers';
    } else if (path.startsWith('/customers/')) {
      return 'Customer Details';
    } else if (path === '/subscription') {
      return 'Subscription Plans';
    } else if (path === '/apiKeys') {
      return 'API Keys';
    } else if (path === '/chatService') {
      return 'Chat Service';
    } else if (path === '/adminHub') {
      return 'Admin Hub';
    } else if (path === '/onboarding') {
      return `${APP_CONFIG.NAME} Onboarding`;
    }
    return `${APP_CONFIG.NAME} Dashboard`; // Default fallback
  };

  /**
   * Opens the sidebar navigation
   * Called when the menu button is clicked
   */
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  /**
   * Closes the sidebar navigation
   * Called when the sidebar should be hidden
   */
  const handleDrawerClose = () => {
    setOpen(false);
  };

  /**
   * Handles user logout by navigating to the logout page
   */
  const handleLogout = () => {
    navigate('/logout');
  };

  /**
   * Opens the theme selection menu
   * 
   * @param {React.MouseEvent<HTMLElement>} event - The click event that triggered the menu
   */
  const handleThemeMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setThemeMenuAnchor(event.currentTarget);
  };

  /**
   * Closes the theme selection menu
   */
  const handleThemeMenuClose = () => {
    setThemeMenuAnchor(null);
  };

  /**
   * Changes the application theme mode
   * 
   * @param {'system' | 'light' | 'dark'} mode - The new theme mode to apply
   */
  const handleThemeChange = (mode: 'system' | 'light' | 'dark') => {
    setThemeMode(mode);
    handleThemeMenuClose();
  };

  /**
   * Returns the appropriate theme icon based on current theme mode
   * 
   * @returns {JSX.Element} The theme icon component
   */
  const getThemeIcon = () => {
    switch (themeMode) {
      case 'system':
        return <SettingsBrightnessIcon />;
      case 'light':
        return <LightModeIcon />;
      case 'dark':
        return <DarkModeIcon />;
      default:
        return <SettingsBrightnessIcon />;
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline /> {/* Resets CSS for consistent look across browsers */}
      <AppBarStyled position="fixed" open={shouldShowSidebar ? open : false} headerOnly={!shouldShowSidebar} sx={{ 
        backgroundColor: isDarkMode ? 'background.paper' : 'white', 
        color: 'text.primary' 
      }}>
        <Toolbar>
          {shouldShowSidebar && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={open ? handleDrawerClose : handleDrawerOpen}
              edge="start"
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" noWrap component="div">
            {getPageTitle()}
          </Typography>

          {/* This Box pushes the following elements to the right */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Theme Selector Dropdown */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              size="large"
              color="inherit"
              onClick={handleThemeMenuOpen}
              aria-label="theme selector"
            >
              {getThemeIcon()}
            </IconButton>
            <Menu
              anchorEl={themeMenuAnchor}
              open={Boolean(themeMenuAnchor)}
              onClose={handleThemeMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem 
                onClick={() => handleThemeChange('system')}
                selected={themeMode === 'system'}
              >
                System
              </MenuItem>
              <MenuItem 
                onClick={() => handleThemeChange('light')}
                selected={themeMode === 'light'}
              >
                Light
              </MenuItem>
              <MenuItem 
                onClick={() => handleThemeChange('dark')}
                selected={themeMode === 'dark'}
              >
                Dark
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBarStyled>
      {shouldShowSidebar ? <Sidebar open={open} handleDrawerClose={handleDrawerClose} handleDrawerOpen={handleDrawerOpen} onLogout={handleLogout} /> : null}
      {/* Main content: single source for left/right spacing (px: 3). Pages should not add their own horizontal padding. */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 4.5,
          px: 4.5,
          boxSizing: 'border-box',
          minWidth: 0,
        }}
      >
        <Toolbar /> {/* This creates necessary padding below the AppBar */}
        {children}
      </Box>
    </Box>
  );
};

export default Layout;